import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { WaitlistCTA } from './WaitlistCTA';
// Authentic full-color brand mark with a robust fallback chain so a tile is
// NEVER blank/broken:
//   1. full-color dashboard-icons (walkxcode)  ->
//   2. simpleicons                             ->
//   3. a brand-tinted monogram chip
type Brand = {
  // dashboard-icons slug (hyphenated where needed), e.g. "microsoft-teams"
  iconSlug: string;
  // simpleicons slug fallback, e.g. "microsoftteams"
  simpleSlug: string;
  name: string;
  // monogram fallback styling
  mono: string;
  brandColor: string;
};
const BRANDS: Record<string, Brand> = {
  slack: {
    iconSlug: 'slack',
    simpleSlug: 'slack',
    name: 'Slack',
    mono: 'S',
    brandColor: '#611f69'
  },
  teams: {
    iconSlug: 'microsoft-teams',
    simpleSlug: 'microsoftteams',
    name: 'Teams',
    mono: 'T',
    brandColor: '#4b53bc'
  },
  hubspot: {
    iconSlug: 'hubspot',
    simpleSlug: 'hubspot',
    name: 'HubSpot',
    mono: 'H',
    brandColor: '#ff7a59'
  },
  notion: {
    iconSlug: 'notion',
    simpleSlug: 'notion',
    name: 'Notion',
    mono: 'N',
    brandColor: '#2B211A'
  },
  salesforce: {
    iconSlug: 'salesforce',
    simpleSlug: 'salesforce',
    name: 'Salesforce',
    mono: 'S',
    brandColor: '#00A1E0'
  },
  linear: {
    iconSlug: 'linear',
    simpleSlug: 'linear',
    name: 'Linear',
    mono: 'L',
    brandColor: '#5E6AD2'
  }
};
function BrandLogo({
  brand,
  className = 'w-5 h-5'
}: {
  brand: keyof typeof BRANDS;
  className?: string;
}) {
  const b = BRANDS[brand];
  // 0 = dashboard-icons, 1 = simpleicons, 2 = monogram
  const [stage, setStage] = useState(0);
  if (stage >= 2) {
    return <span className={`flex items-center justify-center rounded-md text-white font-bold ${className}`} style={{
      backgroundColor: b.brandColor
    }} aria-hidden="true" data-id="element-1181">
        <span className="text-[10px] leading-none" data-id="element-1182">{b.mono}</span>
      </span>;
  }
  const src = stage === 0 ? `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${b.iconSlug}.svg` : `https://cdn.simpleicons.org/${b.simpleSlug}`;
  return <img key={stage} src={src} alt={b.name} className={`${className} object-contain`} loading="lazy" onError={() => setStage(s => s + 1)} data-id="element-1183" />;
}
export function GettingStartedSteps() {
  return <section className="w-full bg-[#FAF8F4] py-24 md:py-32 overflow-hidden" data-id="element-1184">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-1185">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20" data-id="element-1186">
          <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 md:mb-6" data-id="element-1187">
            Plug-and-play
          </span>
          <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1] mb-5 md:mb-6" data-id="element-1188">
            Up and running in 2 minutes
          </h2>
          <p className="text-base md:text-lg text-neutral-500 font-medium leading-relaxed max-w-2xl mx-auto" data-id="element-1189">
            No setup project. No IT ticket. No training videos.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16 md:mb-20" data-id="element-1190">
          {/* Card 1 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.5
        }} className="group flex flex-col bg-white rounded-[2rem] border border-neutral-200/70 shadow-[0_1px_3px_rgba(43,33,26,0.04)] overflow-hidden hover:shadow-[0_24px_60px_-12px_rgba(43,33,26,0.18)] transition-all duration-500 hover:-translate-y-1" data-id="element-1191">
            {/* Visual */}
            <div className="relative h-64 bg-gradient-to-b from-[#221a13] to-[#0E0B08] p-8 flex flex-col items-center justify-center gap-3.5 overflow-hidden" data-id="element-1192">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(234,88,12,0.18),transparent_55%)] opacity-60 group-hover:opacity-100 transition-opacity duration-500" data-id="element-1193" />
              <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/noise.png')] opacity-[0.05] mix-blend-overlay" data-id="element-1194" />
              <div className="absolute inset-x-0 top-0 h-px bg-white/10" data-id="element-1195" />
              <div className="absolute inset-0 shadow-[inset_0_0_60px_rgba(0,0,0,0.4)] pointer-events-none" data-id="element-1196" />

              {/* Slack Button Mockup */}
              <div className="relative z-10 w-full max-w-[240px] bg-white rounded-2xl p-3 flex items-center justify-between shadow-[0_18px_36px_-8px_rgba(0,0,0,0.55)] ring-1 ring-black/5 transform group-hover:-translate-y-1 transition-transform duration-500" data-id="element-1197">
                <div className="flex items-center gap-3" data-id="element-1198">
                  <div className="w-8 h-8 rounded-lg bg-[#F6F3EE] flex items-center justify-center shrink-0 ring-1 ring-black/5" data-id="element-1199">
                    <BrandLogo brand="slack" className="w-4 h-4" data-id="element-1200" />
                  </div>
                  <div className="flex flex-col" data-id="element-1201">
                    <span className="text-sm font-bold text-[#2B211A]" data-id="element-1202">
                      Add to Slack
                    </span>
                    <span className="text-[10px] font-medium text-neutral-400" data-id="element-1203">
                      OAuth · live in minutes
                    </span>
                  </div>
                </div>
                <div className="w-5 h-5 rounded-full bg-green-50 flex items-center justify-center text-green-500 ring-1 ring-green-100" data-id="element-1204">
                  <Check size={12} strokeWidth={3} data-id="element-1205" />
                </div>
              </div>

              <span className="relative z-10 text-white/35 text-[10px] font-semibold uppercase tracking-[0.25em]" data-id="element-1206">
                or
              </span>

              {/* Teams Button Mockup — Teams isn't live yet, so it reads as
               "Coming soon" rather than an OAuth-ready connection. */}
              <div className="relative z-10 w-full max-w-[240px] bg-white rounded-2xl p-3 flex items-center justify-between shadow-[0_18px_36px_-8px_rgba(0,0,0,0.55)] ring-1 ring-black/5 transform group-hover:translate-y-1 transition-transform duration-500" data-id="element-1207">
                <div className="flex items-center gap-3" data-id="element-1208">
                  <div className="w-8 h-8 rounded-lg bg-[#F6F3EE] flex items-center justify-center shrink-0 ring-1 ring-black/5" data-id="element-1209">
                    <BrandLogo brand="teams" className="w-4 h-4" data-id="element-1210" />
                  </div>
                  <div className="flex flex-col" data-id="element-1211">
                    <span className="text-sm font-bold text-[#2B211A]" data-id="element-1212">
                      Add to MS Teams
                    </span>
                    <span className="text-[10px] font-medium text-neutral-400" data-id="element-1213">
                      Coming soon
                    </span>
                  </div>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full bg-neutral-100 text-neutral-400 ring-1 ring-neutral-200" data-id="element-1214">
                  Soon
                </span>
              </div>
            </div>
            {/* Text */}
            <div className="p-6 sm:p-8 md:p-10 flex-1 flex flex-col bg-white" data-id="element-1215">
              <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#EA580C] mb-3" data-id="element-1216">
                Step 01
              </span>
              <h3 className="text-xl sm:text-2xl font-title font-extrabold text-[#2B211A] leading-tight mb-4" data-id="element-1217">
                Add to Slack or Teams
              </h3>
              <p className="text-neutral-500 leading-relaxed text-sm md:text-base font-medium" data-id="element-1218">
                Install Giga from the app directory. It shows up like any new
                teammate, ready to talk.
              </p>
            </div>
          </motion.div>

          {/* Card 2 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="group flex flex-col bg-white rounded-[2rem] border border-neutral-200/70 shadow-[0_1px_3px_rgba(43,33,26,0.04)] overflow-hidden hover:shadow-[0_24px_60px_-12px_rgba(43,33,26,0.18)] transition-all duration-500 hover:-translate-y-1" data-id="element-1219">
            {/* Visual */}
            <div className="relative h-64 bg-gradient-to-b from-[#221a13] to-[#0E0B08] flex items-center justify-center overflow-hidden" data-id="element-1220">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(234,88,12,0.2),transparent_70%)] opacity-60 group-hover:opacity-100 transition-opacity duration-500" data-id="element-1221" />
              <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/noise.png')] opacity-[0.05] mix-blend-overlay" data-id="element-1222" />
              <div className="absolute inset-x-0 top-0 h-px bg-white/10" data-id="element-1223" />
              <div className="absolute inset-0 shadow-[inset_0_0_60px_rgba(0,0,0,0.4)] pointer-events-none" data-id="element-1224" />

              {/* Concentric guide rings */}
              <div className="absolute w-[210px] h-[210px] rounded-full border border-white/[0.06]" data-id="element-1225" />
              <div className="absolute w-[150px] h-[150px] rounded-full border border-white/[0.08]" data-id="element-1226" />

              {/* Connective lines from each tool to the core */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 300 256" fill="none" preserveAspectRatio="xMidYMid meet" aria-hidden="true" data-id="element-1227">
                {[[78, 64], [60, 184], [222, 72], [232, 200]].map(([x, y], i) => <line key={i} x1="150" y1="128" x2={x} y2={y} stroke="url(#gigaLine)" strokeWidth="1.5" strokeDasharray="3 4" data-id="element-1228" />)}
                <defs data-id="element-1229">
                  <linearGradient id="gigaLine" x1="0" y1="0" x2="1" y2="0" data-id="element-1230">
                    <stop offset="0%" stopColor="rgba(234,88,12,0.5)" data-id="element-1231" />
                    <stop offset="100%" stopColor="rgba(234,88,12,0)" data-id="element-1232" />
                  </linearGradient>
                </defs>
              </svg>

              {/* Center Logo */}
              <div className="relative z-20 w-16 h-16 rounded-2xl bg-[#EA580C] flex items-center justify-center shadow-[0_0_50px_rgba(234,88,12,0.55),inset_0_2px_4px_rgba(255,255,255,0.35)] ring-1 ring-white/25 transform group-hover:scale-105 transition-transform duration-500" data-id="element-1233">
                <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="Giga" className="w-8 h-8 object-contain [filter:brightness(0)_invert(1)]" data-id="element-1234" />
              </div>

              {/* Orbiting Icons */}
              <div className="absolute inset-0 z-10 flex items-center justify-center" data-id="element-1235">
                <div className="absolute top-[15%] left-[20%] w-11 h-11 rounded-xl bg-white flex items-center justify-center shadow-[0_14px_28px_-6px_rgba(0,0,0,0.6)] ring-1 ring-black/5 rotate-[-8deg] transform group-hover:-translate-y-2 group-hover:-translate-x-2 transition-transform duration-500" data-id="element-1236">
                  <BrandLogo brand="hubspot" className="w-5 h-5" data-id="element-1237" />
                </div>
                <div className="absolute bottom-[20%] left-[15%] w-11 h-11 rounded-xl bg-white flex items-center justify-center shadow-[0_14px_28px_-6px_rgba(0,0,0,0.6)] ring-1 ring-black/5 rotate-[5deg] transform group-hover:translate-y-2 group-hover:-translate-x-2 transition-transform duration-500" data-id="element-1238">
                  <BrandLogo brand="notion" className="w-5 h-5" data-id="element-1239" />
                </div>
                <div className="absolute top-[20%] right-[15%] w-11 h-11 rounded-xl bg-white flex items-center justify-center shadow-[0_14px_28px_-6px_rgba(0,0,0,0.6)] ring-1 ring-black/5 rotate-[12deg] transform group-hover:-translate-y-2 group-hover:translate-x-2 transition-transform duration-500" data-id="element-1240">
                  <BrandLogo brand="salesforce" className="w-5 h-5" data-id="element-1241" />
                </div>
                <div className="absolute bottom-[15%] right-[20%] w-11 h-11 rounded-xl bg-white flex items-center justify-center shadow-[0_14px_28px_-6px_rgba(0,0,0,0.6)] ring-1 ring-black/5 rotate-[-5deg] transform group-hover:translate-y-2 group-hover:translate-x-2 transition-transform duration-500" data-id="element-1242">
                  <BrandLogo brand="linear" className="w-5 h-5" data-id="element-1243" />
                </div>
              </div>
            </div>
            {/* Text */}
            <div className="p-6 sm:p-8 md:p-10 flex-1 flex flex-col bg-white" data-id="element-1244">
              <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#EA580C] mb-3" data-id="element-1245">
                Step 02
              </span>
              <h3 className="text-xl sm:text-2xl font-title font-extrabold text-[#2B211A] leading-tight mb-4" data-id="element-1246">
                Connect your tools
              </h3>
              <p className="text-neutral-500 leading-relaxed text-sm md:text-base font-medium" data-id="element-1247">
                Link your CRM, Drive, calendar, and analytics. Each connection
                lets Giga act, not just read.
              </p>
            </div>
          </motion.div>

          {/* Card 3 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="group flex flex-col bg-white rounded-[2rem] border border-neutral-200/70 shadow-[0_1px_3px_rgba(43,33,26,0.04)] overflow-hidden hover:shadow-[0_24px_60px_-12px_rgba(43,33,26,0.18)] transition-all duration-500 hover:-translate-y-1" data-id="element-1248">
            {/* Visual */}
            <div className="relative h-64 bg-gradient-to-b from-[#221a13] to-[#0E0B08] flex items-end justify-center px-6 pt-8 overflow-hidden" data-id="element-1249">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(234,88,12,0.18),transparent_60%)] opacity-60 group-hover:opacity-100 transition-opacity duration-500" data-id="element-1250" />
              <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/noise.png')] opacity-[0.05] mix-blend-overlay" data-id="element-1251" />
              <div className="absolute inset-x-0 top-0 h-px bg-white/10" data-id="element-1252" />
              <div className="absolute inset-0 shadow-[inset_0_0_60px_rgba(0,0,0,0.4)] pointer-events-none" data-id="element-1253" />

              {/* Slack Exchange Mockup — the user assigns the job by
               @-mentioning Giga in a channel, then Giga replies with the
               finished result. */}
              <div className="relative z-10 w-full max-w-[280px] bg-white rounded-t-2xl p-5 shadow-[0_-18px_36px_-8px_rgba(0,0,0,0.55)] ring-1 ring-black/5 translate-y-2 transform group-hover:translate-y-0 transition-transform duration-500" data-id="element-1254">
                {/* User assigning the task */}
                <div className="flex items-start gap-3" data-id="element-1255">
                  <div className="w-8 h-8 rounded-lg shrink-0 mt-0.5 overflow-hidden ring-1 ring-black/5" data-id="element-1256">
                    <img src="https://i.pravatar.cc/100?img=12" alt="" aria-hidden="true" className="w-full h-full object-cover" data-id="element-1257" />
                  </div>
                  <div className="flex flex-col gap-1 w-full" data-id="element-1258">
                    <div className="flex items-baseline gap-2" data-id="element-1259">
                      <span className="font-bold text-[#2B211A] text-sm" data-id="element-1260">
                        Dana
                      </span>
                      <span className="text-[10px] font-medium text-neutral-400" data-id="element-1261">
                        6:58 AM
                      </span>
                    </div>
                    <p className="text-xs text-[#2B211A] leading-relaxed font-medium" data-id="element-1262">
                      <span className="text-[#1264A3] font-semibold" data-id="element-1263">
                        @Giga
                      </span>{' '}
                      find us a cheaper packaging supplier and summarize the
                      options.
                    </p>
                  </div>
                </div>

                {/* Giga's reply */}
                <div className="flex items-start gap-3 mt-4" data-id="element-1264">
                  <div className="w-8 h-8 rounded-lg bg-[#EA580C] flex items-center justify-center shrink-0 mt-0.5 shadow-[0_4px_12px_rgba(234,88,12,0.35),inset_0_2px_4px_rgba(255,255,255,0.35)] ring-1 ring-white/25" data-id="element-1265">
                    <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="Giga" className="w-4 h-4 object-contain [filter:brightness(0)_invert(1)]" data-id="element-1266" />
                  </div>
                  <div className="flex flex-col gap-1.5 w-full" data-id="element-1267">
                    <div className="flex items-baseline gap-2" data-id="element-1268">
                      <span className="font-bold text-[#2B211A] text-sm" data-id="element-1269">
                        Giga
                      </span>
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500" data-id="element-1270">
                        APP
                      </span>
                      <span className="text-[10px] font-medium text-neutral-400" data-id="element-1271">
                        7:00 AM
                      </span>
                    </div>
                    <p className="text-xs text-[#2B211A] leading-relaxed font-medium" data-id="element-1272">
                      Done. Compared 7 suppliers, found one with &gt;40%
                      savings.
                    </p>
                    <span className="text-[10px] font-medium text-blue-600 hover:underline cursor-pointer" data-id="element-1273">
                      acme-analysis.giga.space
                    </span>

                    {/* Mini Chart */}
                    <div className="mt-2 w-full bg-[#FAF8F4] border border-neutral-100 rounded-xl p-3 shadow-inner" data-id="element-1274">
                      <div className="flex justify-between items-end h-10 gap-1.5" data-id="element-1275">
                        {[40, 70, 45, 90, 60, 30, 80].map((h, i) => <div key={i} className="w-full bg-blue-500 rounded-t-sm transition-all duration-700 ease-out" style={{
                        height: `${h}%`,
                        opacity: 0.8 + i * 0.03
                      }} data-id="element-1276" />)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Text */}
            <div className="p-6 sm:p-8 md:p-10 flex-1 flex flex-col bg-white" data-id="element-1277">
              <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#EA580C] mb-3" data-id="element-1278">
                Step 03
              </span>
              <h3 className="text-xl sm:text-2xl font-title font-extrabold text-[#2B211A] leading-tight mb-4" data-id="element-1279">
                Assign your first job
              </h3>
              <p className="text-neutral-500 leading-relaxed text-sm md:text-base font-medium" data-id="element-1280">
                Give Giga a task that eats your week. It does it, then runs it
                on a schedule if you want.
              </p>
            </div>
          </motion.div>
        </div>

        {/* CTA */}
        <div className="flex justify-center" data-id="element-1281">
          <WaitlistCTA className="inline-flex items-center justify-center rounded-full bg-[#EA580C] px-8 py-4 text-base font-bold text-white shadow-lg shadow-[#EA580C]/20 transition-all duration-300 hover:bg-[#d24e09] hover:scale-105" data-id="element-1282">
            Get started for free
          </WaitlistCTA>
        </div>
      </div>
    </section>;
}